import {
	IconArticle,
	IconDots,
	IconSearch,
	IconShoppingBag,
	IconSpeakerphone,
	IconThumbUp,
	IconUsers,
	IconWorldWww,
} from "@tabler/icons-react"
import { motion, useReducedMotion } from "motion/react"
import { type ComponentType, type FC, useState } from "react"
import { Navigate, useSearchParams } from "react-router"

import { type PlatformFindSource } from "@forento/core/models/platform"
import dayjs from "@forento/core/utilities/dayjs"

import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

import Layout, { Logo } from "../Layout"
import ChoiceCard from "../Layout/ChoiceCard"

const SelectFindSourcePage: FC = () => {
	const user = useUser()
	const platform = usePlatform()!
	const [searchParams] = useSearchParams()
	const prefersReducedMotion = useReducedMotion()

	const [startDate] = useState(dayjs())
	const [submittingId, setSubmittingId] = useState<PlatformFindSource>()

	const options: {
		id: PlatformFindSource
		label: string
		icon: ComponentType<{ className?: string }>
		accent: string
	}[] = [
		{ id: "landing", label: "Forento's website", icon: IconWorldWww, accent: "bg-[#dff4ff] text-[#176b91]" },
		{ id: "search-engine", label: "Search engine", icon: IconSearch, accent: "bg-[#fff0bc] text-[#8a5d00]" },
		{
			id: "marketplace",
			label: "Software marketplace",
			icon: IconShoppingBag,
			accent: "bg-[#dff7e8] text-[#167246]",
		},
		{ id: "friend", label: "Friend", icon: IconUsers, accent: "bg-[#fce7f3] text-[#9d2464]" },
		{ id: "blog", label: "Blog", icon: IconArticle, accent: "bg-[#e5e8ff] text-[#4452a8]" },
		{ id: "influencer", label: "Influencer", icon: IconSpeakerphone, accent: "bg-[#ffe3dc] text-[#a5412b]" },
		{ id: "social-media", label: "Social media", icon: IconThumbUp, accent: "bg-[#f4e7ff] text-[#7540a4]" },
		{ id: "other", label: "Other", icon: IconDots, accent: "bg-slate-100 text-slate-600" },
	]

	if (platform.hasChosenFindSource) {
		return <Navigate to={searchParams.get("next") ?? routes.account.selectIntent()} />
	}

	async function handleSelect(id: PlatformFindSource) {
		setSubmittingId(id)
		try {
			await trpc.platform.setFindSource.mutate({
				source: id,
				answerTime: Math.floor(dayjs().diff(startDate, "milliseconds") / 10) / 100,
			})
			await user.reload()
		} catch (error) {
			console.error(error)
			setSubmittingId(undefined)
		}
	}

	return (
		<Layout>
			<main className="flex w-full max-w-2xl flex-col items-center py-8 sm:py-12">
				<Logo className="mb-8" />
				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					className="flex max-w-xl flex-col items-center text-center"
				>
					<p className="mb-3 rounded-full bg-[#ecfccb] px-3 py-1 text-xs font-bold tracking-[0.16em] text-[#4d7c0f] uppercase">
						Quick question
					</p>
					<h1 className="text-3xl leading-tight font-bold tracking-tight text-slate-950 sm:text-4xl">
						How did you hear about us?
					</h1>
					<p className="mt-3 text-base leading-relaxed font-medium text-slate-500">
						It helps us understand what's working. One tap and you're through.
					</p>
				</motion.div>

				<div className="mt-9 grid w-full gap-3 sm:grid-cols-2">
					{options.map((option, index) => (
						<motion.div
							key={option.id}
							initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.15 + index * 0.05, duration: 0.35 }}
						>
							<ChoiceCard
								title={option.label}
								icon={option.icon}
								accent={option.accent}
								onClick={() => handleSelect(option.id)}
								isLoading={submittingId === option.id}
								isDisabled={submittingId !== undefined}
							/>
						</motion.div>
					))}
				</div>
			</main>
		</Layout>
	)
}

export default SelectFindSourcePage
